/** @jsxImportSource @emotion/react */
import React, { createContext } from 'react'
import { createFetchContextProps } from '../contextFetch/FetchContext'
import { createContextFetchLoader } from '../contextFetch/Loader'
import { FetchContextType } from '../contextFetch/types'
import {
  ActiveAccountsType,
  UserAccountsType,
  UserAccountFetchHookParamsType,
  UserDetailsType,
  DealerDetailsType,
  DealerDetailsFetchHookParamsType
} from './types'
import { useUserAccounts, useUserDetails, useDealerDetails } from './hooks'

/**
 * this function generates a user accounts context
 * @return {React.Context} - the active account / recent accounts context context
 */
const initialContext: ActiveAccountsType = {
  activeAccount: null,
  recentAccounts: []
}
export function createUserActiveAccountContext(): React.Context<
  ActiveAccountsType
> {
  return createContext(initialContext)
}

/**
 * this function generates a user accounts context
 * @return {React.Context} - the user accounts context
 */
export function createUserAccountsContext(): React.Context<
  FetchContextType<UserAccountsType>
> {
  return createContext(createFetchContextProps<UserAccountsType>())
}

/**
 * this function generates a dealer details context
 * @return {React.Context} - the user accounts context
 */
export function createDealerDetailsContext(): React.Context<
  FetchContextType<DealerDetailsType>
> {
  return createContext(createFetchContextProps<DealerDetailsType>())
}

/**
 * this function generates a user data context
 * @return {React.Context} - the user details context
 */
export function createUserDetailsContext(): React.Context<
  FetchContextType<UserDetailsType>
> {
  return createContext(createFetchContextProps<UserDetailsType>())
}

export const ActiveAccountContext = createUserActiveAccountContext()

export const UserAccountsContext = createUserAccountsContext()

export const DealerDetailsContext = createDealerDetailsContext()

export const UserDetailsContext = createUserDetailsContext()

export const UserAccountsFetcher = createContextFetchLoader<
  UserAccountsType,
  UserAccountFetchHookParamsType
>({
  fetchHook: useUserAccounts,
  FetchContext: UserAccountsContext
})

export const UserDetailsFetcher = createContextFetchLoader<UserDetailsType>({
  fetchHook: useUserDetails,
  FetchContext: UserDetailsContext
})

export const DealerDetailsFetcher = createContextFetchLoader<
  DealerDetailsType,
  DealerDetailsFetchHookParamsType
>({
  fetchHook: useDealerDetails,
  FetchContext: DealerDetailsContext
})
